import { useMemo, useState } from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { fmtTRY } from "@/lib/apiClient";
import { SortableHeader, TablePagination, useSortablePagination } from "@/components/SortableTable";

export const currentMonth = () => new Date().toISOString().slice(0, 7);

export const monthOptions = (items = [], dateKeys = ["date", "due_date", "invoice_date"], minMonth = "") => {
  const values = new Set([currentMonth()]);
  items.forEach((item) => dateKeys.forEach((key) => {
    const value = String(item?.[key] || "").slice(0, 7);
    if (/^\d{4}-\d{2}$/.test(value)) values.add(value);
  }));
  return [...values].filter((value) => !minMonth || value >= minMonth).sort().reverse();
};

export const monthLabel = (value) => {
  if (!value) return "Tüm aylar";
  const [year, month] = value.split("-");
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
};

export function TableFilters({ search, setSearch, month, setMonth, items, hideSearch = false, minMonth = "" }) {
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
      {!hideSearch && (
        <label className="relative min-w-0 sm:w-72">
          <MagnifyingGlass size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Tabloda ara" className="input-flat w-full pl-9" />
        </label>
      )}
      <select value={month} onChange={(e) => setMonth(e.target.value)} className="input-flat min-w-44">
        <option value="">Tüm aylar</option>
        {monthOptions(items, undefined, minMonth).map((value) => <option key={value} value={value}>{monthLabel(value)}</option>)}
      </select>
    </div>
  );
}

const cellValue = (item, column) => typeof column.value === "function" ? column.value(item) : item?.[column.key];

export function WorldTable({ items, columns, empty = "Henüz kayıt yok.", rowKey = "id", showTotals = true }) {
  const [pageSize, setPageSize] = useState(10);
  const dateColumn = columns.find((column) => ["date", "due_date", "payment_date", "invoice_date", "statement_date"].includes(column.key));
  const defaultSort = dateColumn ? { key: dateColumn.sortValue || dateColumn.value || dateColumn.key, direction: "asc" } : null;
  const { pageItems, page, pageCount, totalItems, sort, requestSort, setPage } = useSortablePagination(items, pageSize, defaultSort);
  const numericColumns = columns.filter((column) => column.numeric);
  const totals = useMemo(() => Object.fromEntries(numericColumns.map((column) => [column.key, items.reduce((sum, item) => sum + Number(cellValue(item, column) || 0), 0)])), [items, numericColumns]);
  const renderCell = (item, column) => column.render ? column.render(item) : (cellValue(item, column) ?? "-");
  const primaryColumn = columns[0];
  return (
    <div className="card-flat overflow-hidden">
      <div className="flex flex-wrap items-center gap-1 border-b border-[#E5E5E5] px-3 py-2" aria-label="Görünen satır sayısı">
        {[10, 25, 50, 100].map((size) => <button key={size} type="button" onClick={() => { setPageSize(size); setPage(1); }} className={`rounded px-2 py-1 text-xs font-bold ${pageSize === size ? "bg-neutral-900 text-white" : "text-neutral-500 hover:bg-neutral-100"}`}>{size}</button>)}
      </div>
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-neutral-50 border-b border-[#E5E5E5]">
            <tr>{columns.map((column) => (
              <SortableHeader key={column.key} label={column.label} sortKey={column.sortValue || column.value || column.key} sort={sort} onSort={requestSort} align={column.align} className={column.className || ""} />
            ))}</tr>
          </thead>
          <tbody className="divide-y divide-[#ECECEC]">
            {pageItems.map((item, index) => (
              <tr key={item?.[rowKey] || index} className="hover:bg-neutral-50/70">
                {columns.map((column) => (
                  <td key={column.key} className={`px-4 py-3 align-middle ${column.align === "right" ? "text-right" : ""}`}>
                    {renderCell(item, column)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {showTotals && numericColumns.length > 0 && <tfoot className="border-t-2 border-[#D8D8D8] bg-neutral-50 font-bold">
            <tr>{columns.map((column, index) => <td key={column.key} className={`px-4 py-3 ${column.align === "right" ? "text-right" : ""}`}>{index === 0 ? "Toplam" : column.numeric ? fmtTRY(totals[column.key]) : ""}</td>)}</tr>
          </tfoot>}
        </table>
      </div>
      <div className="divide-y divide-[#ECECEC] sm:hidden">
        {pageItems.map((item, index) => <article key={item?.[rowKey] || index} className="p-4">
          <div className="mb-3 min-w-0"><div className="label-mini mb-1">{primaryColumn.label || "Kayıt"}</div><div className="break-words font-bold">{renderCell(item, primaryColumn)}</div></div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">{columns.slice(1).map((column) => <div key={column.key} className={column.label ? "min-w-0" : "col-span-2 flex justify-end"}><div className={column.label ? "label-mini mb-1" : "sr-only"}>{column.label || "İşlem"}</div><div className={`min-w-0 break-words text-sm ${column.align === "right" ? "text-right font-semibold" : ""}`}>{renderCell(item, column)}</div></div>)}</div>
        </article>)}
      </div>
      {showTotals && numericColumns.length > 0 && <div className="border-t-2 border-[#D8D8D8] bg-neutral-50 p-4 sm:hidden"><div className="label-mini mb-3">Toplam</div><div className="grid grid-cols-2 gap-x-4 gap-y-3">{numericColumns.map((column) => <div key={column.key}><div className="label-mini mb-1">{column.label}</div><div className="font-bold">{fmtTRY(totals[column.key])}</div></div>)}</div></div>}
      {!pageItems.length && <div className="p-10 text-center text-sm text-neutral-500">{empty}</div>}
      <TablePagination page={page} pageCount={pageCount} pageSize={pageSize} totalItems={totalItems} onPageChange={setPage} />
    </div>
  );
}

export const useWorldFilter = (items, dateKeys = ["date", "due_date", "invoice_date"]) => {
  const [search, setSearch] = useState("");
  const [month, setMonth] = useState("");
  const filtered = useMemo(() => items.filter((item) => {
    const textMatch = !search || Object.values(item).some((value) => String(value ?? "").toLocaleLowerCase("tr").includes(search.toLocaleLowerCase("tr")));
    const monthMatch = !month || dateKeys.some((key) => String(item?.[key] || "").startsWith(month));
    return textMatch && monthMatch;
  }), [items, search, month, dateKeys]);
  return { search, setSearch, month, setMonth, filtered };
};

export const SummaryCards = ({ cards }) => (
  <div className={`grid gap-3 ${cards.length > 2 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
    {cards.map((card) => (
      <div key={card.label} className="card-flat p-4 sm:p-5 min-w-0">
        <div className="label-mini mb-2">{card.label}</div>
        <div className={`stat-value text-2xl sm:text-3xl ${card.tone || ""}`}>{card.value}</div>
        {card.detail && <div className="mt-2 text-xs text-neutral-500">{card.detail}</div>}
      </div>
    ))}
  </div>
);
